import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import {
  loadOwnerLillyCapabilities,
  sendOwnerLillyMessage,
} from "../lib/lilly";


export default function OwnerLillyPage() {
  const { user, loading: authLoading } = useAuth();
  const [capabilityState, setCapabilityState] = useState(null);
  const [capabilityError, setCapabilityError] = useState("");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (authLoading || user?.role !== "owner") return;
    let active = true;
    loadOwnerLillyCapabilities()
      .then((result) => {
        if (active) setCapabilityState(result);
      })
      .catch(() => {
        if (active) {
          setCapabilityError("The private Lilly service is unavailable.");
        }
      });
    return () => {
      active = false;
    };
  }, [authLoading, user]);

  if (authLoading) {
    return <main className="min-h-[75vh] px-6 py-20">Checking owner session…</main>;
  }
  if (user?.role !== "owner") {
    return <Navigate to="/owner/login" replace />;
  }

  const submit = async (event) => {
    event.preventDefault();
    const outgoing = message.trim();
    if (!outgoing || sending) return;
    setSending(true);
    setMessage("");
    setMessages((current) => [
      ...current,
      { kind: "owner", text: outgoing },
    ]);
    try {
      const response = await sendOwnerLillyMessage(outgoing);
      setMessages((current) => [
        ...current,
        {
          kind: "lilly",
          text: response.answer,
          metadata: response,
        },
      ]);
    } catch {
      setMessages((current) => [
        ...current,
        {
          kind: "failure",
          text: "The private Lilly request failed.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-[75vh] px-4 py-10 sm:px-6">
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[300px_1fr]">
        <aside className="border border-[var(--gu-border)] bg-[var(--gu-surface)] p-5">
          <p className="overline text-[var(--gu-muted)]">Current actor</p>
          <p className="mt-2 font-display text-3xl">
            {(capabilityState?.actor?.role || user.role).toUpperCase()}
          </p>

          <h2 className="overline mt-8 text-[var(--gu-muted)]">
            Registered capability state
          </h2>
          {capabilityError && (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {capabilityError}
            </p>
          )}
          <div className="mt-3 space-y-3">
            {(capabilityState?.capabilities || []).map((capability) => (
              <div
                key={capability.tool}
                className="border border-[var(--gu-border)] bg-[var(--gu-bg)] p-3"
              >
                <p className="text-sm font-medium">{capability.capability}</p>
                <p className="mt-1 text-xs uppercase tracking-wide text-[var(--gu-muted)]">
                  {capability.status}
                </p>
              </div>
            ))}
          </div>
        </aside>

        <section className="flex min-h-[65vh] flex-col border border-[var(--gu-border)] bg-[var(--gu-bg)]">
          <header className="border-b border-[var(--gu-border)] p-5">
            <p className="overline text-[var(--gu-muted)]">Private owner test environment</p>
            <h1 className="mt-2 font-display text-4xl">Lilly</h1>
          </header>

          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            {messages.map((item, index) => (
              <article
                key={`${item.kind}-${index}`}
                className={`max-w-[90%] border p-4 ${
                  item.kind === "owner"
                    ? "ml-auto border-[var(--gu-primary)] bg-[var(--gu-surface-2)]"
                    : "border-[var(--gu-border)] bg-[var(--gu-surface)]"
                }`}
              >
                <p className="whitespace-pre-wrap">{item.text}</p>
                {item.metadata && (
                  <details className="mt-3 text-xs text-[var(--gu-muted)]">
                    <summary>Governed request details</summary>
                    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(
                        {
                          analysis_status: item.metadata.analysis_status,
                          execution_status: item.metadata.execution_status,
                          intents: item.metadata.intents,
                          tool_results: item.metadata.tool_results,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </details>
                )}
              </article>
            ))}
          </div>

          <form
            onSubmit={submit}
            className="flex gap-3 border-t border-[var(--gu-border)] bg-[var(--gu-surface)] p-5"
          >
            <label className="sr-only" htmlFor="owner-lilly-message">
              Message Lilly
            </label>
            <textarea
              id="owner-lilly-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={2}
              maxLength={8000}
              placeholder="Message Lilly"
              className="min-w-0 flex-1 resize-none border border-[var(--gu-border)] bg-[var(--gu-bg)] px-4 py-3 outline-none focus:ring-2 focus:ring-[var(--gu-accent)]"
            />
            <button
              type="submit"
              disabled={sending || !message.trim()}
              className="self-stretch bg-[var(--gu-primary)] px-5 text-[var(--gu-bg)] disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
